import Link from "next/link";
import type { CityLandingLink, StationLink } from "@/lib/seo/product-breadcrumb";

/**
 * "이 상품을 빌릴 수 있는 곳" — indexed city × category landings (with this
 * product's branch count inside each) and up to five station pages. Rendered
 * in the server HTML at the head of the location section. Nothing when the
 * product has no landing and no physical branch (delivery-only products).
 */
export default function ProductPlaceLinks({
  title,
  cityLandings,
  stations,
  countLabel,
  className,
  model = null,
}: {
  title: string;
  cityLandings: readonly CityLandingLink[];
  stations: readonly StationLink[];
  countLabel: (count: number) => string;
  className?: string;
  /** 모델 허브 칩("{모델} 전체 보기") — 같은 모델의 다른 색상·지점 등급 상품을 한 페이지에서 */
  model?: { label: string; path: string } | null;
}) {
  if (cityLandings.length === 0 && stations.length === 0 && !model) return null;
  const chip =
    "inline-flex items-baseline gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[13px] leading-[18px] font-medium text-gray-900 transition-colors hover:border-gray-300 hover:bg-gray-50";
  return (
    <div className={`grid gap-2.5 rounded-xl bg-gray-50 p-3 ${className ?? ""}`}>
      <p className="text-[13px] leading-[18px] font-medium text-gray-700">{title}</p>
      {model && (
        <ul className="flex flex-wrap gap-2">
          <li>
            <Link href={model.path} className={chip}>
              {model.label}
            </Link>
          </li>
        </ul>
      )}
      {cityLandings.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {cityLandings.map((landing) => (
            <li key={landing.path}>
              <Link href={landing.path} className={chip}>
                {landing.label}
                <span className="font-normal tabular-nums text-gray-500">
                  {countLabel(landing.count)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {stations.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {stations.map((station) => (
            <li key={station.id}>
              <Link href={station.path} className={chip}>
                {station.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
